import { useEffect, useRef, useState } from 'react';
import { requestJSON } from '../lib/request';
import { readStorage, writeStorage } from '../lib/browserStorage';
import { defaultDesign, canonicalDesign, geometryKey, designErrors } from './rules';
import { defaultPlacement } from '../photo/placement';
import { switchProductDesign } from './productPreview';
import { upgradeBifoldDraft } from './bifoldSettings';
import { syncOperationDraft } from './operationSync';

export function useConfigurator(searchParams, setSearchParams) {
  const [products, setProducts] = useState([]), [finishes, setFinishes] = useState([]);
  const [design, setDesign] = useState(null), [draft, setDraft] = useState(null);
  const [error, setError] = useState(''), [saving, setSaving] = useState(false), [saved, setSaved] = useState(false);
  const [id, setId] = useState(null);
  const loadedQuery = useRef(null);
  const modelParam = searchParams.get('model'), designParam = searchParams.get('design'), finishParam = searchParams.get('finish'), sharedParam = searchParams.get('shared');
  useEffect(() => {
    const queryKey = JSON.stringify([modelParam, designParam, finishParam, sharedParam]);
    if (loadedQuery.current === queryKey) return;
    const controller = new AbortController();
    const load = async () => {
      try {
        const [all, colors] = await Promise.all([requestJSON('/products', { signal: controller.signal }), requestJSON('/finishes', { signal: controller.signal })]);
        const models = all.filter(p => p.family === 'MT150' || p.id === 'sd-150' || p.is_fixed || ['bifold', 'swing', 'pivot'].includes(p.kind)).map(p => p.id === 'sd-150' ? { ...p, short_label: 'Sliding door Element', track_count: 2 } : p);
        if (modelParam && !models.some(p => p.id === modelParam)) throw new Error('This model is no longer in the collection. Return to the showroom to choose another model.');
        const initialModel = models.some(p => p.id === modelParam) ? modelParam : 'mt150-double';
        let initial = defaultDesign(initialModel), savedId = designParam || sharedParam || (!modelParam && !sharedParam ? readStorage('lumen-configuration') : null);
        if (colors.some(f => f.id === finishParam)) initial.finish = finishParam;
        if (savedId) {
          try {
            const restored = await requestJSON(`/configurations/${encodeURIComponent(savedId)}`, { signal: controller.signal });
            const { id: restoredId, updated_at, ...configuration } = restored;
            if (sharedParam && !designParam) { initial = { ...configuration, photo_overlay: null }; savedId = null; } else { initial = configuration; savedId = restoredId; }
          } catch (restoreError) {
            if (controller.signal.aborted) return;
            if (designParam || sharedParam) throw restoreError;
            savedId = null;
          }
        }
        if (controller.signal.aborted) return;
        loadedQuery.current = queryKey;
        const editable = upgradeBifoldDraft(initial);
        setProducts(models); setFinishes(colors); setDesign(initial); setDraft(editable); setId(savedId); setSaved(!!savedId && editable === initial);
      } catch (loadError) { if (!controller.signal.aborted) setError(loadError.name === 'TimeoutError' ? 'The preview took too long to load. Please try again.' : loadError.message); }
    };
    load(); return () => controller.abort();
  }, [modelParam, designParam, finishParam, sharedParam]);
  const pending = !!draft && (geometryKey(draft) !== geometryKey(design) || draft.finish !== design.finish);
  const edit = update => { setDraft(update); setSaved(false); };
  const apply = () => {
    const next = canonicalDesign(draft);
    if (next.layout !== design.layout && next.photo_overlay) next.photo_overlay = {
      ...next.photo_overlay, opening_regions: [],
      placement: { ...next.photo_overlay.placement, yaw: defaultPlacement(next.layout).yaw },
    };
    setDesign(next); setDraft(next); setSaved(false);
  };
  const operate = (key, values) => {
    setDesign(previous => ({ ...previous, [key]: { ...previous[key], ...values } }));
    setDraft(previous => syncOperationDraft(previous, design, key, values));
    setSaved(false);
  };
  const reset = () => { const next = { ...defaultDesign(design.model_id), photo_overlay: design.photo_overlay || null }; setDesign(next); setDraft(next); setSaved(false); };
  const setPhoto = record => {
    const overlay = record ? { photo_id: record.id, placement: defaultPlacement(design.layout) } : null;
    setDesign(previous => ({ ...previous, photo_overlay: overlay })); setDraft(previous => ({ ...previous, photo_overlay: overlay })); setSaved(false);
  };
  const placePhoto = patch => {
    const update = previous => previous.photo_overlay ? { ...previous, photo_overlay: { ...previous.photo_overlay, placement: { ...previous.photo_overlay.placement, ...patch } } } : previous;
    setDesign(update); setDraft(update); setSaved(false);
  };
  const photoFinish = finish => { setDesign(previous => ({ ...previous, finish })); setDraft(previous => ({ ...previous, finish })); setSaved(false); };
  const setRegions = opening_regions => {
    const update = previous => previous.photo_overlay ? { ...previous, photo_overlay: { ...previous.photo_overlay, opening_regions } } : previous;
    setDesign(update); setDraft(update); setSaved(false);
  };
  const replaceModel = modelId => {
    const next = switchProductDesign(design, modelId, defaultDesign);
    if (next.layout !== design.layout && next.photo_overlay) next.photo_overlay = {
      ...next.photo_overlay, opening_regions: [],
      placement: { ...next.photo_overlay.placement, yaw: defaultPlacement(next.layout).yaw },
    };
    const error = Object.values(designErrors(next))[0]; if (error) throw new Error(`${error} Adjust the size or bars in Studio before changing the model.`);
    setDesign(next); setDraft(next); setSaved(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      const nextId = id || crypto.randomUUID();
      await requestJSON(`/configurations/${nextId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(design) });
      writeStorage('lumen-configuration', nextId); setId(nextId); setSaved(true);
      // The design remains reloadable even when embedded browser storage is blocked.
      loadedQuery.current = JSON.stringify([null, nextId, null, null]);
      setSearchParams({ design: nextId }, { replace: true });
      return nextId;
    } finally { setSaving(false); }
  };
  return { id, products, finishes, design, draft, setDraft: edit, error, pending, saving, saved, apply, operate, reset, save, setPhoto, placePhoto, photoFinish, setRegions, replaceModel };
}